// @flow
import React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {usePrefs} from 'docs/data';
import Group from 'sail/Group';
import {preloadedQuery} from 'sail/Search';
import type {Query} from 'sail/Search';
import Box from 'sail/Box';
import {InputGroup} from 'sail/Input';
import {ButtonLink} from 'sail/Button';
import {external} from 'sail/Icon/Assets';
import {ResourceSelector} from './ResourceSelector';
import {ActionSelector} from './ActionSelector';
import {Params} from './Params';
import {CodegenExample} from './CodegenExample';
import {getResources, getFlattenedResourceNames} from './resources';
import {MakeRequestButton} from './MakeRequestButton';
import {ApiExplorerProvider, useApiExplorer} from './ApiExplorerProvider';
import {ApiResponse} from './ApiResponse';
import {dimensions} from './StyleHelpers';

const getSearchableText = (item) => item;

// Get list of resources from CLUI's generated_tree
const resources = getResources();
// Resource Selection
const IGNORED_RESOURCES = ['listen', 'trigger'];
const resource_names = getFlattenedResourceNames(resources, IGNORED_RESOURCES);

const queryResources: Query<string> = preloadedQuery(
  getSearchableText,
  resource_names,
);

const messages = defineMessages({
  resource: {
    id: 'docsApp.apiExplorer.resource',
    description: 'Label on resource selector',
    defaultMessage: 'Resource',
  },
  action: {
    id: 'docsApp.apiExplorer.action',
    description: 'Label on action selector',
    defaultMessage: 'Action',
  },
  request: {
    id: 'docsApp.apiExplorer.request',
    description: 'Label on generated example snippets',
    defaultMessage: 'Request',
  },
  response: {
    id: 'docsApp.apiExplorer.response',
    description: 'Label on API response',
    defaultMessage: 'Response',
  },
});

type Props = {
  embedded?: boolean,
};

const ApiExplorerForm = ({embedded}: Props) => {
  const {
    state: {resource, action},
  } = useApiExplorer();
  const {getPref} = usePrefs();
  const lang = getPref('lang') || '';

  return (
    <form onFocus={(event) => event.stopPropagation()}>
      <Group direction="horizontal" spacing={24}>
        <Group direction="vertical" spacing={24}>
          <Box
            flex={{direction: 'column'}}
            style={{width: dimensions.width * 2}}
          >
            <Box flex={{direction: 'row'}} padding={{vertical: 12}}>
              <Box style={{width: 240}}>
                <FormattedMessage {...messages.resource} />
              </Box>
              <Box style={{width: 240}}>
                <FormattedMessage {...messages.action} />
              </Box>
            </Box>
            <Group direction="horizontal" justifyContent="space-between">
              <InputGroup direction="horizontal">
                <ResourceSelector
                  resource_names={resource_names}
                  queryResources={queryResources}
                  getSearchableText={getSearchableText}
                />
                <ActionSelector resources={resources} />
              </InputGroup>
              <MakeRequestButton resources={resources} embedded />
            </Group>
          </Box>
          {resource && action && (
            <ButtonLink
              icon={external}
              display="inline-flex"
              label="Visit API reference"
              href={
                lang
                  ? `https://stripe.com/docs/api/${resource}/${action}?lang=${lang}`
                  : `https://stripe.com/docs/api/${resource}/${action}`
              }
            />
          )}
          {resource && action && <Params resources={resources} />}
        </Group>

        {!embedded && (
          <Box flex={{direction: 'column'}}>
            <Box flex={{direction: 'column'}}>
              <FormattedMessage {...messages.request} />
              <CodegenExample />
            </Box>
            <Box flex={{direction: 'column'}}>
              <FormattedMessage {...messages.response} />
              <ApiResponse />
            </Box>
          </Box>
        )}
      </Group>
    </form>
  );
};

export const ApiExplorer = ({embedded}: Props) => {
  return (
    <ApiExplorerProvider>
      <ApiExplorerForm embedded={embedded} />
    </ApiExplorerProvider>
  );
};
